'use client';
import React, { useState, useEffect, useContext } from 'react';
import Paper from '@mui/material/Paper';
import { usePathname } from 'next/navigation';
import RenderNavItems from './Items';
import { useNavItems } from '@/app/hooks/useNavItems';
import { MAX_SCREEN_WIDTH } from '@/app/constants/layoutConfig';
import { useTheme } from '@mui/material';
import { UserContext } from '@/app/contexts/user-context';
import { appHandlesSafeArea } from '@/app/helpers/appInsets';

// Hook to detect Android Samsung devices with portrait-secondary orientation
const usePortraitSecondaryPadding = () => {
    const [padding, setPadding] = useState('0 16px 8px');
    const userContext = useContext(UserContext);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const checkAndSetPadding = () => {
            // App builds that pad the WebView by the real safe-area inset need no
            // correction here; only the old hardcoded-30dp builds do.
            if (appHandlesSafeArea()) {
                setPadding('0 16px 8px');
                return;
            }

            const isAndroid = /Android/i.test(navigator.userAgent);
            const hasSamsungPrefix = /SM-/.test(navigator.userAgent);
            const hasHighDPR = window.devicePixelRatio >= 2.625;
            const screenOrientation = window.screen?.orientation?.type || 'unknown';
            const isPortraitSecondary = screenOrientation === 'portrait-secondary';

            // If all conditions met: 21px 16px 84px, otherwise: 0 16px 8px
            let paddingValue = '0 16px 8px';
            if (isAndroid && hasSamsungPrefix && hasHighDPR && isPortraitSecondary) {
                paddingValue = '34px 16px 84px';
            }

            setPadding(paddingValue);
        };

        checkAndSetPadding();

        // Listen for orientation changes and window resize
        window.addEventListener('orientationchange', checkAndSetPadding);
        window.addEventListener('resize', checkAndSetPadding);

        return () => {
            window.removeEventListener('orientationchange', checkAndSetPadding);
            window.removeEventListener('resize', checkAndSetPadding);
        };
    }, [userContext?.user?.id]);

    return padding;
};

const Navigation = () => {
    const pathname = usePathname();
    const theme = useTheme();
    const navItems = useNavItems();
    const padding = usePortraitSecondaryPadding();

    return (
        <Paper sx={{ ...styles.Paper, background: theme.palette.background.default, alignItems: 'center', padding }} elevation={5}>
            <RenderNavItems pathname={pathname} items={navItems} />
        </Paper>
    );
};

const styles: { Paper: React.CSSProperties } = {
    Paper: {
        boxShadow: '1px 0px 4px 0px rgba(0, 0, 0, 0.04)',
        borderRadius: '0px',
        position: 'fixed',
        bottom: 0,
        margin: '0 auto',
        width: '100%',
        maxWidth: MAX_SCREEN_WIDTH,
        left: 0,
        justifyContent: 'space-between',
        alignItems: 'center',
        display: 'flex',
        right: 0,
        height: '78px',
        zIndex: 99,
    },
};

export default Navigation;
